import React from "react";
import { AbsoluteFill, Audio, interpolate, Sequence, staticFile } from "remotion";
import { Sfx, SfxContext } from "../b2b/ui/Sfx";
import { COPY, CopyContext, type Lang } from "./copy";
import { LOOK } from "./look";
import { type B2CFormat, StageFrame } from "./stage";
import { Brands } from "./scenes/Brands";
import { Cash } from "./scenes/Cash";
import { Cta } from "./scenes/Cta";
import { Earn } from "./scenes/Earn";
import { Followers } from "./scenes/Followers";
import { Hook } from "./scenes/Hook";
import { Pick } from "./scenes/Pick";
import { Share } from "./scenes/Share";
import { Trust } from "./scenes/Trust";
import { Unpaid } from "./scenes/Unpaid";
import {
  B2C_DURATION,
  B2C_FPS,
  SCENE_ORDER,
  SCENE_START,
  type SceneKey,
  sceneFrames,
} from "./timing";

/**
 * `sfx`: sound effects on top of the music (default on). The landing plays
 * muted, social feeds play with sound: the effects are for them. Pass
 * `false` for a music-only render (mix check: render `--codec=wav` both
 * ways and subtract).
 */
export type B2CVideoProps = { lang: Lang; format?: B2CFormat; sfx?: boolean };

export const B2C_SCENES: Record<SceneKey, React.FC> = {
  hook: Hook,
  trust: Trust,
  unpaid: Unpaid,
  followers: Followers,
  pick: Pick,
  share: Share,
  earn: Earn,
  cash: Cash,
  brands: Brands,
  cta: Cta,
};

/**
 * B2C landing video (~39s; square master + portrait / App Store /
 * landscape, see `stage.tsx`). Sits in the "3 steps" section of the
 * ambassador page, so it dramatises the steps instead of re-listing them:
 *
 *   hook → why it matters (88 %) → the injustice (0 €) → Frak (+5 €)
 *   → no followers needed → 1 pick / 2 share / 3 both win
 *   → real cash, to your bank → brands that pay → CTA
 *
 * Every scene is a hard cut on the beat (see `timing.ts`). Sound effects
 * use the B2B palette (`b2b/ui/Sfx`, same mix as the merchant video): cut
 * whooshes here, every other cue in its scene next to the constant that
 * drives the visual.
 */
export const B2CVideo: React.FC<B2CVideoProps> = ({ lang, format = "square", sfx = true }) => {
  return (
    <CopyContext.Provider value={COPY[lang]}>
      <SfxContext.Provider value={sfx}>
      <AbsoluteFill style={{ background: LOOK.sky }}>
        <Audio
          src={staticFile("song.mp3")}
          volume={(f) =>
            interpolate(f, [0, 6, B2C_DURATION - 20, B2C_DURATION], [0, 1, 1, 0], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            }) * 0.3
          }
        />
        <StageFrame format={format}>
        {SCENE_ORDER.map((key) => {
          const Scene = B2C_SCENES[key];
          return (
            <Sequence
              key={key}
              name={key}
              from={SCENE_START[key]}
              durationInFrames={sceneFrames(key)}
              premountFor={B2C_FPS}
            >
              <Scene />
            </Sequence>
          );
        })}
        </StageFrame>
        {/* Cut whooshes land on the cut (they start a little before it). */}
        {SCENE_ORDER.slice(1).map((key) => (
          <Sfx key={key} sound="swoosh" at={SCENE_START[key]} volume={0.8} />
        ))}
      </AbsoluteFill>
      </SfxContext.Provider>
    </CopyContext.Provider>
  );
};
